import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../../api/client'
import PlacesMap, {
    PlaceMapItem,
    UnnamedPin,
    PlacesMapViewMode,
} from '../places/PlacesMap'
import LocationDetailModal, {
    LocationDetailValue,
} from '../checkin/LocationDetailModal'
import ChartTimeRangePicker from '../../charts/ChartTimeRangePicker'
import { toLocalDateStr } from '../../utils/dates'
import {
    useTimeRangeState,
    windowForAnchor,
    isAllTime,
} from '../../charts/windowRange'

/**
 * Per-project Places section. Shows the places visited on this project's
 * checkins on a heat-/cluster-map, with the same calendar-aligned time
 * range picker the charts use (W/M/6M/Y/All). Refetches on window change
 * — the backend does the filtering against checkins.applies_to and the
 * project_id, so the frontend just hands it a half-open [start, end)
 * range derived from the picker.
 *
 * Lifetime data-bearing dates come back on every response (independent of
 * the date filter) so the picker can skip empty windows on prev/next.
 */

interface ProjectPlacesSectionProps {
    projectId: number
    onLifetimeEmptyChange?: (empty: boolean) => void
}

interface PlacesMapResponse {
    places: PlaceMapItem[]
    unnamed_pins: UnnamedPin[]
    checkin_dates: string[]
}

const ProjectPlacesSection: React.FC<ProjectPlacesSectionProps> = ({ projectId, onLifetimeEmptyChange }) => {
    const [places, setPlaces] = useState<PlaceMapItem[]>([])
    const [unnamedPins, setUnnamedPins] = useState<UnnamedPin[]>([])
    const [checkinDates, setCheckinDates] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [viewMode, setViewMode] = useState<PlacesMapViewMode>('heatmap')

    const [selectedPlace, setSelectedPlace] = useState<PlaceMapItem | null>(null)
    const [detailCoords, setDetailCoords] = useState<{ lat: number; lng: number } | null>(null)

    // The picker is data-aware: it needs every checkin date in scope to
    // page prev/next past empty windows. We seed dataDates from the lifetime
    // `checkin_dates` returned alongside the map data on every response.
    const dateMs = useMemo(
        () => checkinDates.map((d) => Date.parse(d)).filter((n) => !Number.isNaN(n)),
        [checkinDates],
    )
    const range = useTimeRangeState(dateMs)
    const win = useMemo(
        () => windowForAnchor(range.granularity, range.anchor),
        [range.granularity, range.anchor],
    )

    // Refetch on window change. Use win.start/win.end as the primitive deps
    // — they're stable across renders that keep the same calendar window,
    // so the snap-to-latest anchor adjustment doesn't force a redundant
    // fetch when the new anchor lands in the same month/week/etc.
    useEffect(() => {
        let cancelled = false
        const params = new URLSearchParams()
        params.set('project_id', String(projectId))
        if (!isAllTime(range.granularity)) {
            params.set('start_date', toLocalDateStr(win.start))
            params.set('end_date', toLocalDateStr(win.end))
        }
        setLoading(true)
        api.get<PlacesMapResponse>(`/api/places/map?${params.toString()}`)
            .then((resp) => {
                if (cancelled) return
                setPlaces(resp.data.places)
                setUnnamedPins(resp.data.unnamed_pins)
                setCheckinDates(resp.data.checkin_dates || [])
                setError(null)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load project places:', err)
                setError('Failed to load places.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [projectId, range.granularity, win.start, win.end])

    const detailValue = useMemo<LocationDetailValue | null>(() => {
        if (selectedPlace) {
            return {
                collectionItemId: selectedPlace.id,
                lat: selectedPlace.lat ?? undefined,
                lng: selectedPlace.lng ?? undefined,
                name: selectedPlace.name,
                address: selectedPlace.address,
                googlePlaceId: selectedPlace.google_place_id,
                categoryName: selectedPlace.category_name,
                categorySlug: selectedPlace.category_slug,
            }
        }
        if (detailCoords) {
            return { lat: detailCoords.lat, lng: detailCoords.lng }
        }
        return null
    }, [selectedPlace, detailCoords])

    const hasAnyData = places.length > 0 || unnamedPins.length > 0
    // Lifetime-empty: no location-bearing checkins on the project at all.
    // Distinct from "this window happens to be empty" — that one keeps the
    // picker visible so the user can page back.
    const lifetimeEmpty = !loading && checkinDates.length === 0

    useEffect(() => {
        if (loading) return
        onLifetimeEmptyChange?.(lifetimeEmpty)
    }, [loading, lifetimeEmpty, onLifetimeEmptyChange])

    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-2">
                <h2 className="text-2xl font-semibold text-gray-800">Places</h2>
                {!lifetimeEmpty && (
                    <ViewToggle value={viewMode} onChange={setViewMode} />
                )}
            </div>

            {lifetimeEmpty ? (
                <div className="text-gray-500 text-sm py-8 text-center">
                    No places visited on this project's checkins yet.
                </div>
            ) : (
                <>
                    <ChartTimeRangePicker state={range} />
                    {error ? (
                        <div className="text-rose-500 text-sm py-4">{error}</div>
                    ) : !hasAnyData && !loading ? (
                        <div className="text-gray-500 text-sm py-8 text-center">
                            No places visited in this period.
                        </div>
                    ) : (
                        <PlacesMap
                            places={places}
                            unnamedPins={unnamedPins}
                            viewMode={viewMode}
                            loading={loading}
                            heightClass="h-[210px]"
                            onPlaceClick={(p) => {
                                setDetailCoords(null)
                                setSelectedPlace(p)
                            }}
                            onPinClick={(c) => {
                                setSelectedPlace(null)
                                setDetailCoords(c)
                            }}
                        />
                    )}
                </>
            )}

            <LocationDetailModal
                open={detailValue != null}
                value={detailValue}
                onClose={() => {
                    setSelectedPlace(null)
                    setDetailCoords(null)
                }}
            />
        </div>
    )
}

const ViewToggle: React.FC<{
    value: PlacesMapViewMode
    onChange: (v: PlacesMapViewMode) => void
}> = ({ value, onChange }) => (
    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs">
        <button
            type="button"
            onClick={() => onChange('heatmap')}
            className={`px-2.5 py-1 ${
                value === 'heatmap'
                    ? 'bg-action text-plum'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
        >
            Heatmap
        </button>
        <button
            type="button"
            onClick={() => onChange('clusters')}
            className={`px-2.5 py-1 border-l border-gray-300 ${
                value === 'clusters'
                    ? 'bg-action text-plum'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
        >
            Clusters
        </button>
    </div>
)

export default ProjectPlacesSection
